import { FileDBValue, isResizedType, UnuploadedFileDBValue } from '@hb/shared'
import { FirebaseStorage, FullMetadata, getDownloadURL, getMetadata, ref } from 'firebase/storage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { storage as mainAppStorage } from '../../../backend/storage'
import { useFirebaseApp } from '../../../contexts/FirebaseAppProvider'

export const getFileUrl = async (
  path: string,
  storage?: FirebaseStorage,
): Promise<{
  url?: string
  metadata?: FullMetadata
}> => {
  const fileRef = ref(storage ?? mainAppStorage, path)
  const [metadata, url] = await Promise.all([getMetadata(fileRef), getDownloadURL(fileRef)])

  if (!url) throw new Error('Error reading file')
  return { url, metadata }
}
export interface UseFileData {
  path?: string | null
  pathLoading?: boolean
  noFetch?: boolean
  value?: FileDBValue | UnuploadedFileDBValue | null
}

export const useFile = ({ path, pathLoading, noFetch, value }: UseFileData) => {
  const [url, setUrl] = useState<string | undefined>()
  const { storage: customStorage } = useFirebaseApp()
  const [metadata, setMetadata] = useState<FullMetadata | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [blob, setBlob] = useState<Blob | undefined>()
  const [blobErr, setBlobErr] = useState<string | undefined>()
  const [blobLoading, setBlobLoading] = useState(false)
  const [loading, setLoading] = useState(false)

  const [waitingForResize, setWaitingForResize] = useState<number | null>(null)
  useEffect(() => {
    if (!value) return
    const modifiedOn = Math.max(value?.uploadedOn ?? 0, value?.movedOn ?? 0)
    if (!modifiedOn) return setWaitingForResize(null)
    if (!isResizedType(value.type)) return setWaitingForResize(null)
    const timeUntilLoad = modifiedOn + 1000 * 5 - Date.now()
    if (timeUntilLoad > 0) return setWaitingForResize(timeUntilLoad)
    return setWaitingForResize(null)
  }, [value])

  useEffect(() => {
    if (waitingForResize) {
      const t = setTimeout(() => {
        setWaitingForResize(null)
      }, waitingForResize)
      return () => clearTimeout(t)
    }
    return () => {}
  }, [waitingForResize])

  // Track uploadedOn in a ref to avoid recalculating shouldRefetch on every render
  const uploadedOnRef = useRef<number | undefined>(value?.uploadedOn)
  useEffect(() => {
    uploadedOnRef.current = value?.uploadedOn
  }, [value?.uploadedOn])

  const { dataUrl } = value ?? {}

  const mounted = useRef(false)
  const numRetriesRef = useRef(0)

  const fetchFile = useCallback(() => {
    setUrl(undefined)
    setBlob(undefined)
    numRetriesRef.current = 0
    if (path && !pathLoading && !waitingForResize) {
      const refetch = () => {
        // Compute shouldRefetch inside the callback to avoid dependency issues
        const uploadedOn = uploadedOnRef.current
        const shouldRefetch = uploadedOn ? Date.now() - uploadedOn < 1000 * 5 : false

        setLoading(true)
        getFileUrl(path, customStorage)
          .then(({ url: fetchedUrl, metadata: fetchedMetadata }) => {
            if (mounted.current) {
              if (!fetchedUrl && !fetchedMetadata) {
                if (!shouldRefetch || numRetriesRef.current > 3) {
                  setLoading(false)
                } else {
                  setTimeout(() => {
                    numRetriesRef.current += 1
                    refetch()
                  }, 1500)
                }
              } else if (fetchedUrl) {
                setLoading(false)
                if (!noFetch) {
                  setBlobLoading(true)
                  fetch(fetchedUrl)
                    .then(res => {
                      res
                        .blob()
                        .then(resBlob => {
                          setBlobLoading(false)
                          setBlob(resBlob)
                        })
                        .catch(bErr => {
                          setBlobLoading(false)
                          console.error(bErr)
                          setBlobErr('Error fetching file')
                        })
                    })
                    .catch(fetchErr => {
                      if (!shouldRefetch || numRetriesRef.current > 3) {
                        console.error(fetchErr)
                        setBlobLoading(false)
                      } else {
                        setTimeout(() => {
                          numRetriesRef.current += 1
                          refetch()
                        }, 1500)
                      }
                    })
                }

                if (fetchedMetadata) setMetadata(fetchedMetadata)
                setUrl(fetchedUrl)
              }
            }
          })
          .catch(err => {
            if (mounted.current) {
              if (!shouldRefetch || numRetriesRef.current > 3) {
                setBlobLoading(false)
                setLoading(false)
                console.error(err)
                setError('error fetching')
                setUrl(undefined)
                setMetadata(undefined)
              } else {
                setTimeout(() => {
                  numRetriesRef.current += 1
                  refetch()
                }, 1500)
              }
            }
          })
      }
      refetch()
    } else if (dataUrl) {
      setUrl(dataUrl)
      setBlobLoading(true)
      fetch(dataUrl)
        .then(res => {
          res.blob().then(resBlob => {
            setBlobLoading(false)
            setBlob(resBlob)
          })
        })
        .catch(err => {
          console.error(err)
        })
    }
  }, [path, pathLoading, noFetch, dataUrl, waitingForResize, customStorage])

  useEffect(() => {
    mounted.current = true
    fetchFile()
    return () => {
      mounted.current = false
    }
  }, [fetchFile])

  return useMemo(
    () => ({
      url,
      metadata,
      error,
      loading: loading || !!waitingForResize,
      fetchFile,
      blob,
      blobErr,
      blobLoading,
    }),
    [url, metadata, error, loading, blob, blobErr, blobLoading, waitingForResize, fetchFile],
  )
}
